/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * TablePermissionsListComponent
 * -----------------------------
 * Lists per-table permissions for a given application user.
 *
 * Columns:
 *  - Table (application_table_display_name)
 *  - Assigned Permissions (comma-separated, e.g. "Read, Create")
 *  - Actions (Edit button)
 *
 * Props:
 * - permissions: Array of table-permission objects
 * - page: Current page number
 * - pageCount: Total number of pages
 * - onCreate: Handler → Grant Permission
 * - onEdit: Handler → Edit selected permission record
 * - onPage: Pagination handler for previous/next/page navigation
 */
import React, { useEffect, useState } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import Paginator from '../common/Paginator';
import Button from '../Button';

function TablePermissionsListComponent({
  permissions = [],
  page = 1,
  pageCount = 1,
  onCreate,
  onEdit,
  onPage,
}) {
  const { theme } = useTheme();

  // Responsive: Switch to stacked view at 991px or below
  const [isStacked, setIsStacked] = useState(window.innerWidth <= 991);

  useEffect(() => {
    function handleResize() {
      setIsStacked(window.innerWidth <= 991);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  function handleCreate() {
    if (onCreate) onCreate();
  }

  function handleEditLocal(permission) {
    if (onEdit) onEdit(permission);
  }

  const cardStyle = {
    // UPDATED: use 100% width with max-width to avoid fixed width overflow
    width: '100%',
    background: '#fff',
    borderRadius: theme.cardRadius,
    boxShadow: '0 6px 32px 0 rgba(80,80,180,0.10)',
    // UPDATED: Reduce padding on small screens
    padding: isStacked ? '12px' : '2em',
    // UPDATED: margin auto with side margins for spacing
    margin: isStacked ? '16px auto' : '32px auto',
    position: 'relative',
    boxSizing: 'border-box', // Ensure padding is included in width
  };

  const containerStyle = {
    minHeight: '80vh',
    background: '#f6f8fa',
    paddingTop: 20,
    paddingBottom: 40,
    paddingLeft: isStacked ? 16 : 32, // Add side padding to container
    paddingRight: isStacked ? 16 : 32,
  };

  const actionsColStyle = {
    position: 'sticky',
    right: 0,
    background: '#fff',
    zIndex: 2,
    boxShadow: '0 0 0 1px #eee',
  };

  const formatPermissions = (perm) => {
    const list = perm.permissions || [];
    if (!list.length) {
      if (perm.default_inherit) return 'No access (all actions disabled)';
      return 'None';
    }
    return list
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(', ');
  };

  /**
   * Render a single card for stacked mode
   */
  const renderStackedCard = (perm) => (
    <div
      key={perm.id}
      style={{
        border: `1px solid ${theme.primary}20`,
        borderRadius: 8,
        padding: '16px',
        marginBottom: '16px',
        background: '#fff',
      }}
    >
      {/* Header: Table Name + Edit Action (Flexbox) */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 12,
          gap: 16, // Ensure space between title and edit button
        }}
      >
        <div style={{ flex: 1 }}>
          <strong style={{ color: theme.primary, display: 'block', marginBottom: 4 }}>
            Table
          </strong>
          <div style={{ fontSize: 15, fontWeight: 500, wordBreak: 'break-word' }}>
            {perm.application_table_display_name || perm.application_table_name || ''}
          </div>
        </div>

        {/* Edit Action - Icon + Text */}
        <div
          onClick={() => handleEditLocal(perm)}
          style={{
            cursor: 'pointer',
            color: theme.primary,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 14,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            paddingTop: 4, // Align slightly with text
          }}
          role="button"
          tabIndex={0}
        >
          <i className="bi bi-pencil-square" style={{ fontSize: 16 }} />
          <span>Edit</span>
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <strong style={{ color: '#666', fontSize: 13, display: 'block', marginBottom: 2 }}>
          Assigned Permissions
        </strong>
        <div>{formatPermissions(perm)}</div>
      </div>

      <div style={{ marginBottom: 4 }}>
        <strong style={{ color: '#666', fontSize: 13, display: 'block', marginBottom: 6 }}>
          Data Scope
        </strong>
        {perm.data_scope === 'all' ? (
          <span
            style={{
              display: 'inline-block',
              padding: '4px 10px',
              borderRadius: 12,
              fontSize: 12,
              backgroundColor: '#e0f7e9',
              color: '#0f6b2f',
              fontWeight: 600,
            }}
          >
            All records
          </span>
        ) : (
          <span
            style={{
              display: 'inline-block',
              padding: '4px 10px',
              borderRadius: 12,
              fontSize: 12,
              backgroundColor: '#e8f0ff',
              color: '#174ea6',
              fontWeight: 600,
            }}
          >
            Own records
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h2 className="fw-bold mb-4" style={{ color: theme.primary }}>
          Assign or Modify Permissions for {permissions[0]?.application_user_username}
        </h2>

        {/* Top bar with Grant Permission button */}
        <div className="mb-3">
          <div
            className={`d-flex ${isStacked ? 'flex-column' : 'flex-row'} align-items-stretch`}
            style={{ gap: isStacked ? 0 : 26 }}
          >
            <div className={isStacked ? 'w-100' : 'flex-grow-1'} />
            <div className={isStacked ? 'w-100' : ''}>
              <Button
                mode="primary-bold"
                onClick={handleCreate}
                style={{
                  width: isStacked ? '100%' : 'auto',
                }}
              >
                Grant Permission
              </Button>
            </div>
          </div>
        </div>

        {isStacked ? (
          /* Stacked View for <= 991px */
          <div style={{ marginTop: 20 }}>
            {permissions.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '22px',
                  color: '#bbb',
                  border: '1px dashed #ccc',
                  borderRadius: 8,
                }}
              >
                No access (all actions disabled)
              </div>
            ) : (
              permissions.map(renderStackedCard)
            )}
          </div>
        ) : (
          /* Table View for > 991px */
          <div className="table-responsive" style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                minWidth: '600px',
                borderCollapse: 'collapse',
                marginBottom: 0,
              }}
            >
              <thead>
                <tr
                  style={{
                    background: theme.primary + '08',
                    color: theme.primary,
                    fontWeight: 600,
                    fontSize: 16,
                  }}
                >
                  <th
                    style={{
                      padding: '12px 11px',
                      textAlign: 'left',
                      borderBottom: `2px solid ${theme.primary}15`,
                    }}
                  >
                    Table
                  </th>
                  <th
                    style={{
                      padding: '12px 11px',
                      textAlign: 'left',
                      borderBottom: `2px solid ${theme.primary}15`,
                    }}
                  >
                    Assigned Permissions
                  </th>
                  <th
                    style={{
                      padding: '12px 11px',
                      textAlign: 'left',
                      borderBottom: `2px solid ${theme.primary}15`,
                    }}
                  >
                    Data Scope
                  </th>
                  <th
                    style={{
                      ...actionsColStyle,
                      minWidth: 90,
                      textAlign: 'center',
                      borderBottom: `2px solid ${theme.primary}15`,
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {permissions.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      style={{
                        textAlign: 'center',
                        padding: '22px',
                        color: '#bbb',
                      }}
                    >
                      No access (all actions disabled)
                    </td>
                  </tr>
                ) : (
                  permissions.map((perm) => (
                    <tr key={perm.id} style={{ fontSize: 15 }}>
                      <td
                        style={{
                          padding: '14px 10px',
                          fontWeight: 500,
                        }}
                      >
                        {perm.application_table_display_name ||
                          perm.application_table_name ||
                          ''}
                      </td>
                      <td
                        style={{
                          padding: '14px 10px',
                        }}
                      >
                        {formatPermissions(perm)}
                      </td>
                      <td
                        style={{
                          padding: '14px 10px',
                        }}
                      >
                        {perm.data_scope === 'all' ? (
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '4px 10px',
                              borderRadius: 12,
                              fontSize: 12,
                              backgroundColor: '#e0f7e9',
                              color: '#0f6b2f',
                              fontWeight: 600,
                            }}
                          >
                            All records
                          </span>
                        ) : (
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '4px 10px',
                              borderRadius: 12,
                              fontSize: 12,
                              backgroundColor: '#e8f0ff',
                              color: '#174ea6',
                              fontWeight: 600,
                            }}
                          >
                            Own records
                          </span>
                        )}
                      </td>
                      <td
                        style={{
                          ...actionsColStyle,
                          padding: '14px 8px',
                          textAlign: 'center',
                        }}
                      >
                        <span
                          style={{
                            marginRight: 7,
                            cursor: 'pointer',
                            color: theme.primary,
                          }}
                          title="Edit Permission"
                          onClick={() => handleEditLocal(perm)}
                          tabIndex={0}
                          aria-label="Edit Permission"
                        >
                          <i className="bi bi-pencil-square" />
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <Paginator
          page={page}
          pageCount={pageCount}
          onPrevious={onPage}
          onPage={onPage}
          onNext={onPage}
          maxPagesToShow={3}
        />

        <div
          style={{
            height: 8,
            width: '100%',
            borderBottomLeftRadius: theme.cardRadius,
            borderBottomRightRadius: theme.cardRadius,
            background: theme.gradientBackground,
            position: 'absolute',
            left: 0,
            bottom: 0,
          }}
        />
      </div>
    </div>
  );
}

export default TablePermissionsListComponent;
